import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    TextField,
    Select,
    FormControl,
    InputLabel,
    Alert,
    Tooltip,
    MenuItem,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon, AutoAwesome as AutoAwesomeIcon, FilterListOutlined as FilterListIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import { AdminPageContainer, FormField } from '../components/admin/sharedStyles';
import PageHeader from '../components/PageHeader';
import SuggestionPayload from '../components/SuggestionPayload';
import SuggestionTarget from '../components/SuggestionTarget';
import { getSuggestions, getSuggestionDetail, approveSuggestion, rejectSuggestion, getSuggestionRecipients, redirectSuggestion, generateSuggestions } from '../redux/ducks/ai';
import { triggerRefresh } from '../redux/slices/refreshTriggers';
import { showAlert } from '../redux/slices/alerts';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { useHasPermission } from '../utils/permissionUtils';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';
import { statusMeta, actionLabel, isReviewable, isRejectable, confidenceLabel, riskMeta } from '../utils/suggestionUtils';

const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const StyledFilterPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const ChipLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: theme.palette.text.secondary,
}));

const TitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const SummaryText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const Section = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2.5),
}));

const ReasoningText = styled(Typography)({
    whiteSpace: 'pre-wrap',
});

const ChipRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}));

const RedirectRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const DialogNotice = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const SpinnerSpacer = styled(CircularProgress)(({ theme }) => ({
    marginRight: theme.spacing(1),
}));

const MobileList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const ItemCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
}));

const CardTop = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
}));

const Field = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    overflowWrap: 'anywhere',
});

const FieldLabel = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
}));

const MobilePagination = styled(TablePagination)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
}));

const STATUS_OPTIONS = [
    { value: '', label: 'Any status' },
    { value: 'pending', label: 'Needs review', param: 'status' },
    { value: 'approved', label: 'Approved', param: 'status' },
    { value: 'auto_approved', label: 'Done automatically', param: 'status' },
    { value: 'rejected', label: 'Rejected', param: 'status' },
    { value: 'executing', label: 'Running', param: 'execution' },
    { value: 'executed', label: 'Done', param: 'execution' },
    { value: 'failed', label: 'Failed', param: 'execution' },
    { value: 'invalid', label: 'Stopped retrying', param: 'execution' },
];

const STATUS_OPTION_BY_VALUE = new Map(STATUS_OPTIONS.map((option) => [option.value, option]));

const matchesStatusFilter = (row, status) => {
    const option = STATUS_OPTION_BY_VALUE.get(status);
    if (!option || !option.param) return true;
    return option.param === 'status' ? row.status === option.value : row.execution?.state === option.value;
};

const userName = (user) => {
    if (!user) return null;
    if (user.fullName) return user.fullName;
    if (user.first_name) return `${user.first_name} ${user.last_name || ''}`.trim();
    return null;
};

const historyNote = (status, executionState) => {
    if (executionState === 'invalid') return 'This was tried several times and failed every time, so it will not be tried again. It is kept here as a record.';
    if (executionState === 'executing') return 'This is being carried out right now — it only takes a moment.';
    if (executionState === 'not_started' && (status === 'approved' || status === 'auto_approved')) return 'This has been approved and is about to be carried out.';
    return 'This suggestion has already been dealt with. It is kept here as a record.';
};

const Suggestions = () => {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const hasPermission = useHasPermission();
    const canGenerate = hasPermission('canSeeManagement');
    const suggestionsRefresh = useSelector((state) => state.refreshTriggers.suggestions);
    const currentUserId = useSelector((state) => state.auth.userId);
    const [searchParams, setSearchParams] = useSearchParams();

    // `items` and `total` move together on every write, so one object keeps them consistent.
    const [list, setList] = useState({ items: [], total: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const { items, total } = list;

    const [scope, setScope] = useState('mine');
    const [status, setStatus] = useState('pending');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);

    const [activeId, setActiveId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [isDetailLoading, setIsDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [conflict, setConflict] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [recipientId, setRecipientId] = useState('');

    // Reviewing rows shrinks `total` under our feet, so `page` can outrun the results. Clamping here rather
    // than in an effect keeps TablePagination from ever being handed an out-of-range page.
    const lastPage = total > 0 ? Math.ceil(total / limit) - 1 : 0;
    const effectivePage = Math.min(page, lastPage);

    const params = useMemo(() => {
        const option = STATUS_OPTION_BY_VALUE.get(status);
        return {
            scope: scope === 'mine' ? 'mine' : undefined,
            status: option?.param === 'status' ? option.value : undefined,
            execution: option?.param === 'execution' ? option.value : undefined,
            start: effectivePage * limit,
            limit,
        };
    }, [scope, status, effectivePage, limit]);

    const loadItems = useCallback(() => {
        setIsLoading(true);
        setLoadError(null);
        dispatch(
            getSuggestions(params, (result) => {
                setIsLoading(false);
                if (!result || !result.ok) {
                    setList({ items: [], total: 0 });
                    setLoadError(result?.message || 'Failed to load suggestions');
                    return;
                }
                setList({ items: result.items, total: result.total });
            })
        );
    }, [dispatch, params]);

    const patchRow = useCallback((updated) => {
        if (!updated?._id) return;
        setList((prev) => ({ ...prev, items: prev.items.map((item) => (item._id === updated._id ? { ...item, ...updated } : item)) }));
    }, []);

    const removeRow = useCallback((id) => {
        if (!id) return;
        setList((prev) => {
            // Events arrive for rows off the current page too; only a row we are showing counts against `total`.
            if (!prev.items.some((item) => item._id === id)) return prev;
            return { items: prev.items.filter((item) => item._id !== id), total: Math.max(prev.total - 1, 0) };
        });
    }, []);

    useEffect(() => {
        loadItems();
    }, [loadItems, suggestionsRefresh]);

    // Settle `page` onto the clamped value so later growth in `total` cannot bounce the user back.
    useEffect(() => {
        if (page !== effectivePage) setPage(effectivePage);
    }, [page, effectivePage]);

    // A late callback from a suggestion the user already navigated away from must not overwrite
    // the one they are looking at now.
    const requestedIdRef = useRef(null);

    const loadDetail = useCallback(
        (id) => {
            requestedIdRef.current = id;
            setIsDetailLoading(true);
            setDetailError(null);
            dispatch(
                getSuggestionDetail(id, (result) => {
                    if (requestedIdRef.current !== id) return;
                    setIsDetailLoading(false);
                    if (!result || !result.ok) {
                        setDetail(null);
                        setDetailError(result?.message || 'We could not load this suggestion.');
                        return;
                    }
                    setDetail({ suggestion: result.suggestion, validity: result.validity });
                    if (result.suggestion?.status !== 'pending') return;
                    dispatch(
                        getSuggestionRecipients(id, (found) => {
                            if (requestedIdRef.current !== id) return;
                            setRecipients(found?.users || []);
                        })
                    );
                })
            );
        },
        [dispatch]
    );

    const openDetail = useCallback(
        (id) => {
            setActiveId(id);
            setDetail(null);
            setReason('');
            setConflict(null);
            setRecipients([]);
            setRecipientId('');
            loadDetail(id);
        },
        [loadDetail]
    );

    const closeDetail = () => {
        if (submitting) return;
        requestedIdRef.current = null;
        setActiveId(null);
        setDetail(null);
        setDetailError(null);
        setConflict(null);
        setReason('');
        setRecipients([]);
        setRecipientId('');
    };

    // The dashboard widget deep-links straight to one row; consume the id and drop it from the URL
    // so a later refresh does not reopen the dialog.
    useEffect(() => {
        const deepLinkId = searchParams.get('suggestion');
        if (deepLinkId) {
            openDetail(deepLinkId);
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadItemsRef = useRef(loadItems);
    loadItemsRef.current = loadItems;
    const reloadTimerRef = useRef(null);

    // A generation run lands many rows at once; coalesce the burst into a single reload.
    const scheduleReload = useCallback(() => {
        if (reloadTimerRef.current) return;
        reloadTimerRef.current = setTimeout(() => {
            reloadTimerRef.current = null;
            loadItemsRef.current();
        }, 1000);
    }, []);

    useEffect(() => () => reloadTimerRef.current && clearTimeout(reloadTimerRef.current), []);

    useSocketEvent('suggestion:new', scheduleReload);

    // The payload has no target or payload names (those depend on who is looking), so it is merged
    // over what we already hold rather than replacing it.
    useSocketEvent('suggestion:updated', (row) => {
        if (!row?._id) return;
        const pmId = row.relevantPm?._id || row.relevantPm;
        const inScope = scope !== 'mine' || String(pmId) === String(currentUserId);
        // "Any status" is the empty filter and matches every row.
        if (inScope && matchesStatusFilter(row, status)) {
            if (items.some((item) => item._id === row._id)) patchRow(row);
            else scheduleReload();
        } else {
            removeRow(row._id);
        }
        if (row._id === requestedIdRef.current) {
            setDetail((prev) => (prev ? { ...prev, suggestion: { ...prev.suggestion, ...row }, validity: null } : prev));
        }
    });

    const broadcastRefresh = () => {
        dispatch(triggerRefresh({ triggerName: 'suggestions' }));
    };

    const handleReviewResult = (result, verb) => {
        setSubmitting(false);
        if (!result) return;
        if (result.suggestion) patchRow(result.suggestion);
        if (result.ok) {
            broadcastRefresh();
            dispatch(showAlert({ severity: 'success', message: `Suggestion ${verb}.` }));
            requestedIdRef.current = null;
            setActiveId(null);
            setDetail(null);
            setReason('');
            return;
        }
        if (result.conflict) {
            setConflict(result.message);
            broadcastRefresh();
            if (activeId) loadDetail(activeId);
        }
    };

    const handleApprove = () => {
        if (!activeId) return;
        setSubmitting(true);
        setConflict(null);
        dispatch(approveSuggestion(activeId, reason.trim() || undefined, (result) => handleReviewResult(result, 'approved and carried out')));
    };

    const handleReject = () => {
        if (!activeId) return;
        setSubmitting(true);
        setConflict(null);
        dispatch(rejectSuggestion(activeId, reason.trim() || undefined, (result) => handleReviewResult(result, 'rejected')));
    };

    const handleRedirect = () => {
        if (!activeId || !recipientId) return;
        const recipient = recipients.find((u) => u._id === recipientId);
        setSubmitting(true);
        setConflict(null);
        dispatch(
            redirectSuggestion(activeId, recipientId, (result) => {
                if (result?.ok && scope === 'mine') removeRow(activeId);
                handleReviewResult(result, `sent to ${userName(recipient) || 'them'}`);
            })
        );
    };

    const handleGenerate = () => {
        setGenerating(true);
        dispatch(
            generateSuggestions((result) => {
                setGenerating(false);
                if (!result || !result.ok) return;
                if (result.data?.dispatched === false) {
                    dispatch(showAlert({ severity: 'info', message: result.data.msg || 'A review run is already in progress. Check back shortly.' }));
                    return;
                }
                dispatch(showAlert({ severity: 'success', message: 'The assistant is reviewing your work now. New suggestions will appear here in a few minutes.' }));
            })
        );
    };

    const handleChangeScope = (value) => {
        if (!value) return;
        setScope(value);
        setPage(0);
    };

    const handleChangeStatus = (value) => {
        setStatus(value);
        setPage(0);
    };

    const active = detail?.suggestion || null;
    const validity = detail?.validity || null;
    const reviewable = active && isReviewable(active.status, active.execution?.state);
    const rejectable = active && isRejectable(active.status);
    const notApplicable = validity && validity.valid === false;
    const currentPmId = active?.relevantPm?._id || active?.relevantPm;
    const otherRecipients = recipients.filter((u) => u._id !== currentPmId);
    const confidence = active && confidenceLabel(active.ai?.score);
    const activeMeta = active ? statusMeta(active.status, active.execution?.state) : null;
    const activeRisk = active ? riskMeta(active.risk) : null;

    const emptyMessage =
        scope === 'mine' && status === 'pending'
            ? 'You are all caught up — nothing is waiting for your review.'
            : 'No suggestions match these filters.';

    return (
        <AdminPageContainer>
            <PageHeader
                title="Suggested Actions"
                subtitle="Work the assistant thinks should happen. Nothing is carried out until you approve it."
                actions={
                    <>
                        <Tooltip title="Reload the list">
                            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadItems}>
                                Refresh
                            </Button>
                        </Tooltip>
                        {canGenerate && (
                            <Tooltip title="Ask the assistant to look at current work now and suggest new actions">
                                <span>
                                    <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleGenerate} disabled={generating}>
                                        {generating ? 'Starting…' : 'Look for new suggestions'}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </>
                }
            />

            <StyledFilterPaper>
                <FilterTitle variant="h6">
                    <FilterListIcon fontSize="small" />
                    Filter Options
                </FilterTitle>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        <ChipRow>
                            <ChipLabel>Show</ChipLabel>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={scope}
                                onChange={(e, value) => value && handleChangeScope(value)}
                                aria-label="Filter suggestions by who they are for"
                            >
                                <ToggleButton value="mine">For me</ToggleButton>
                                <ToggleButton value="all">Everyone</ToggleButton>
                            </ToggleButtonGroup>
                        </ChipRow>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                            <InputLabel id="status-label" shrink>Status</InputLabel>
                            <Select labelId="status-label" displayEmpty notched value={status} label="Status" onChange={(e) => handleChangeStatus(e.target.value)}>
                                {STATUS_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </StyledFilterPaper>

            {isLoading ? (
                <LoadingBox>
                    <CircularProgress />
                </LoadingBox>
            ) : loadError ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        We could not load suggested actions.
                    </Typography>
                    <Button variant="outlined" onClick={loadItems}>
                        Try again
                    </Button>
                </EmptyState>
            ) : total === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                        {emptyMessage}
                    </Typography>
                    {scope === 'mine' && (
                        <Button variant="outlined" onClick={() => handleChangeScope('all')}>
                            See everyone&apos;s suggestions
                        </Button>
                    )}
                </EmptyState>
            ) : isMobile ? (
                <MobileList>
                    {items.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Nothing left on this page. Use the pages below to see the rest.
                        </Typography>
                    )}
                    {items.map((item) => {
                        const meta = statusMeta(item.status, item.execution?.state);
                        const reviewableRow = isReviewable(item.status, item.execution?.state);
                        return (
                            <ItemCard key={item._id}>
                                <CardTop>
                                    <TitleText variant="body2">{item.ai?.title || actionLabel(item.actionType)}</TitleText>
                                    <Tooltip title={meta.description}>
                                        <Chip label={meta.label} size="small" color={meta.color} variant={item.status === 'pending' ? 'filled' : 'outlined'} />
                                    </Tooltip>
                                </CardTop>
                                {item.ai?.summary && <SummaryText variant="caption">{item.ai.summary}</SummaryText>}
                                <Field>
                                    <FieldLabel>What it does</FieldLabel>
                                    <Typography variant="caption">{actionLabel(item.actionType)}</Typography>
                                </Field>
                                <Field>
                                    <FieldLabel>Applies to</FieldLabel>
                                    <SuggestionTarget actionType={item.actionType} target={item.target} showHeading={false} />
                                </Field>
                                <Field>
                                    <FieldLabel>For</FieldLabel>
                                    <Typography variant="caption">{userName(item.relevantPm) || '—'}</Typography>
                                </Field>
                                <Field>
                                    <FieldLabel>Suggested</FieldLabel>
                                    <Tooltip title={formatDateTime(item.createdAt)}>
                                        <Typography variant="caption">{item.createdAt ? formatTimeAgo(item.createdAt) : '—'}</Typography>
                                    </Tooltip>
                                </Field>
                                <Button fullWidth variant={reviewableRow ? 'contained' : 'outlined'} onClick={() => openDetail(item._id)}>
                                    {reviewableRow ? 'Review' : 'View'}
                                </Button>
                            </ItemCard>
                        );
                    })}
                    <MobilePagination
                        component="div"
                        count={total}
                        page={effectivePage}
                        onPageChange={(e, next) => setPage(next)}
                        rowsPerPage={limit}
                        rowsPerPageOptions={[10, 25, 50]}
                        onRowsPerPageChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </MobileList>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Suggestion</strong></TableCell>
                                <TableCell><strong>What it does</strong></TableCell>
                                <TableCell><strong>Applies to</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>For</strong></TableCell>
                                <TableCell><strong>Suggested</strong></TableCell>
                                <TableCell align="right"><strong>Action</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="text.secondary">
                                            Nothing left on this page. Use the pages below to see the rest.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {items.map((item) => {
                                const meta = statusMeta(item.status, item.execution?.state);
                                const reviewableRow = isReviewable(item.status, item.execution?.state);
                                return (
                                    <TableRow key={item._id} hover>
                                        <TableCell>
                                            <TitleText variant="body2">{item.ai?.title || actionLabel(item.actionType)}</TitleText>
                                            {item.ai?.summary && <SummaryText variant="caption">{item.ai.summary}</SummaryText>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{actionLabel(item.actionType)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <SuggestionTarget actionType={item.actionType} target={item.target} showHeading={false} />
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={meta.description}>
                                                <Chip label={meta.label} size="small" color={meta.color} variant={item.status === 'pending' ? 'filled' : 'outlined'} />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{userName(item.relevantPm) || '—'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={formatDateTime(item.createdAt)}>
                                                <Typography variant="caption">{item.createdAt ? formatTimeAgo(item.createdAt) : '—'}</Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button size="small" variant={reviewableRow ? 'contained' : 'outlined'} onClick={() => openDetail(item._id)}>
                                                {reviewableRow ? 'Review' : 'View'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={effectivePage}
                        onPageChange={(e, next) => setPage(next)}
                        rowsPerPage={limit}
                        rowsPerPageOptions={[10, 25, 50]}
                        onRowsPerPageChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            )}

            <Dialog open={!!activeId} onClose={closeDetail} maxWidth="sm" fullWidth>
                <DialogTitle>{active?.ai?.title || 'Review suggested action'}</DialogTitle>
                <DialogContent dividers>
                    {/* Outside the loading branch: re-reading the row must not blink the warning away. */}
                    {conflict && (
                        <DialogNotice severity="warning">
                            <Typography variant="body2">{conflict}</Typography>
                            <Typography variant="caption">
                                This suggestion was made a while ago and things have changed since. Nothing was carried out.
                            </Typography>
                        </DialogNotice>
                    )}
                    {isDetailLoading ? (
                        <LoadingBox>
                            <CircularProgress />
                        </LoadingBox>
                    ) : detailError ? (
                        <EmptyState>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                We could not load this suggestion.
                            </Typography>
                            <Button variant="outlined" onClick={() => activeId && loadDetail(activeId)}>
                                Try again
                            </Button>
                        </EmptyState>
                    ) : active ? (
                        <>
                            <DialogContentText variant="body2">
                                {reviewable
                                    ? 'Check exactly what will happen below, then approve or reject it. Nothing runs until you approve.'
                                    : historyNote(active.status, active.execution?.state)}
                            </DialogContentText>

                            <Section>
                                <ChipRow>
                                    <Tooltip title={activeMeta.description}>
                                        <Chip label={activeMeta.label} size="small" color={activeMeta.color} />
                                    </Tooltip>
                                    <Chip label={actionLabel(active.actionType)} size="small" variant="outlined" />
                                    <Tooltip title={activeRisk.description}>
                                        <Chip label={activeRisk.label} size="small" color={activeRisk.color} variant="outlined" />
                                    </Tooltip>
                                    {confidence && <Chip label={confidence.text} size="small" color={confidence.color} variant="outlined" />}
                                </ChipRow>
                            </Section>

                            <Section>
                                <SuggestionTarget actionType={active.actionType} target={active.target} />
                            </Section>

                            {notApplicable && !conflict && (
                                <Section>
                                    <DialogNotice severity="warning">
                                        <Typography variant="body2">{validity.reason || 'This action is no longer valid.'}</Typography>
                                        <Typography variant="caption">Approving is turned off because this would not apply cleanly any more.</Typography>
                                    </DialogNotice>
                                </Section>
                            )}

                            {active.execution?.state === 'invalid' && !conflict && (
                                <Section>
                                    <DialogNotice severity="error">
                                        <Typography variant="body2">The last attempt failed: {active.execution?.error || 'No reason was recorded.'}</Typography>
                                        <Typography variant="caption">We tried this several times and it failed every time, so we stopped. It will need to be done by hand.</Typography>
                                    </DialogNotice>
                                </Section>
                            )}

                            {active.execution?.state === 'executed' && active.execution?.outcome && (
                                <Section>
                                    <DialogNotice severity="info">
                                        <Typography variant="body2">{active.execution.outcome}</Typography>
                                    </DialogNotice>
                                </Section>
                            )}

                            {active.execution?.state === 'failed' && active.execution?.error && (
                                <Section>
                                    <DialogNotice severity="error">
                                        <Typography variant="body2">It could not be carried out: {active.execution.error}</Typography>
                                        <Typography variant="caption">You can approve it again to retry.</Typography>
                                    </DialogNotice>
                                </Section>
                            )}

                            <Section>
                                <SectionLabel>What will happen</SectionLabel>
                                <SuggestionPayload actionType={active.actionType} payload={active.payload} payloadRefs={active.payloadRefs} />
                            </Section>

                            {(active.ai?.summary || active.ai?.reasoning) && (
                                <>
                                    <Section>
                                        <Divider />
                                    </Section>
                                    <Section>
                                        <SectionLabel>Why the assistant suggested this</SectionLabel>
                                        {active.ai?.summary && <ReasoningText variant="body2" gutterBottom>{active.ai.summary}</ReasoningText>}
                                        {active.ai?.reasoning && (
                                            <ReasoningText variant="body2" color="text.secondary">
                                                {active.ai.reasoning}
                                            </ReasoningText>
                                        )}
                                    </Section>
                                </>
                            )}

                            {active.review?.at && (
                                <Section>
                                    <SectionLabel>Reviewed</SectionLabel>
                                    <Typography variant="body2" color="text.secondary">
                                        {userName(active.review.by) || 'Someone'} on {formatDateTime(active.review.at)}
                                    </Typography>
                                    {active.review.reason && (
                                        <Typography variant="body2" color="text.secondary">
                                            Note: {active.review.reason}
                                        </Typography>
                                    )}
                                </Section>
                            )}

                            {active.status === 'pending' && otherRecipients.length > 0 && (
                                <Section>
                                    <SectionLabel>Not for you?</SectionLabel>
                                    <RedirectRow>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="redirect-label">Send to</InputLabel>
                                            <Select
                                                labelId="redirect-label"
                                                label="Send to"
                                                value={recipientId}
                                                onChange={(e) => setRecipientId(e.target.value)}
                                                disabled={submitting}
                                            >
                                                {otherRecipients.map((u) => (
                                                    <MenuItem key={u._id} value={u._id}>
                                                        {userName(u)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Tooltip title="Move this to their list of suggestions to review. Nothing is carried out.">
                                            <span>
                                                <Button variant="outlined" onClick={handleRedirect} disabled={submitting || !recipientId}>
                                                    Send
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </RedirectRow>
                                </Section>
                            )}

                            {reviewable && (
                                <Section>
                                    <FormField>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            label="Note for the record (optional)"
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            helperText="Saved with your decision. Useful to explain why you approved or rejected this."
                                            disabled={submitting}
                                        />
                                    </FormField>
                                </Section>
                            )}
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {submitting && <SpinnerSpacer size={20} />}
                    <Button onClick={closeDetail} disabled={submitting}>
                        Close
                    </Button>
                    {rejectable && (
                        <Tooltip title="Turn this down. It will not be carried out.">
                            <span>
                                <Button color="error" onClick={handleReject} disabled={submitting}>
                                    Reject
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    {reviewable && (
                        <Tooltip title={notApplicable ? 'This action no longer applies, so it cannot be approved.' : 'Approve and carry this out now'}>
                            <span>
                                <Button variant="contained" onClick={handleApprove} disabled={submitting || notApplicable}>
                                    {active?.execution?.state === 'failed' ? 'Approve & retry' : 'Approve & run'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </DialogActions>
            </Dialog>
        </AdminPageContainer>
    );
};

export default Suggestions;
